import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Award, ExternalLink, Eye, EyeOff, Loader2 } from "lucide-react";

import { api } from "@/services/api";
import type { MyCertificate } from "@/types";

/**
 * The participant's issued certificates (KC-455): each links to its public
 * page and carries a visibility toggle — hiding makes the public URL 404
 * until the participant turns it back on. Renders nothing until a
 * certificate has been issued. A Dominion Free participation badge (KC-497)
 * is listed the same way, under its own name.
 */

const certificateKeys = { mine: ["certificates", "me"] as const };

function formatIssueDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function CertificatesSection() {
  const queryClient = useQueryClient();
  const certificatesQuery = useQuery<MyCertificate[]>({
    queryKey: certificateKeys.mine,
    queryFn: () => api.getMyCertificates(),
  });
  const toggle = useMutation({
    mutationFn: ({ id, visible }: { id: string; visible: boolean }) =>
      api.setCertificateVisibility(id, visible),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: certificateKeys.mine }),
  });

  const certificates = certificatesQuery.data ?? [];
  if (certificatesQuery.isPending || certificates.length === 0) return null;

  return (
    <div className="rounded-2xl border border-content-border bg-white p-6">
      <div className="mb-4 flex items-center gap-2">
        <Award className="h-5 w-5 text-content-muted" />
        <h2 className="font-semibold text-content-foreground">
          {certificates.every((c) => c.kind === "participation_badge") ? "Badges" : "Certificates"}
        </h2>
      </div>
      <ul className="space-y-3">
        {certificates.map((cert) => (
          <li
            key={cert.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-content-border p-4"
          >
            <div>
              <p className="font-medium text-content-foreground">
                {cert.kind === "participation_badge"
                  ? "Participation Badge — Dominion Free taster"
                  : "Dominion Programme"}{" "}
                — {cert.cohort_month} {cert.cohort_year}
              </p>
              <p className="text-sm text-content-muted">
                Issued {formatIssueDate(cert.issue_date)}
                {!cert.visible && " · hidden from the web"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {cert.visible && (
                <a
                  href={cert.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-content-border px-3 py-1.5 text-sm font-medium text-content-foreground hover:bg-gray-50"
                >
                  View <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
              <button
                type="button"
                disabled={toggle.isPending}
                onClick={() => toggle.mutate({ id: cert.id, visible: !cert.visible })}
                className="inline-flex items-center gap-1.5 rounded-lg border border-content-border px-3 py-1.5 text-sm font-medium text-content-foreground hover:bg-gray-50 disabled:opacity-50"
              >
                {toggle.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : cert.visible ? (
                  <EyeOff className="h-3.5 w-3.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
                {cert.visible ? "Hide from the web" : "Make visible"}
              </button>
            </div>
          </li>
        ))}
      </ul>
      {toggle.isError && (
        <p className="mt-3 text-sm text-red-600">
          {toggle.error instanceof Error ? toggle.error.message : "Could not update visibility"}
        </p>
      )}
    </div>
  );
}
